import { useState, useRef, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Calendar, Clock, User, GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STATUS_COLORS = {
  open: { bg: '#4A90E2', text: '#fff' },
  assigned: { bg: '#00A6A0', text: '#fff' },
  in_progress: { bg: '#F97316', text: '#fff' },
  completed: { bg: '#A8E6CF', text: '#1A2329' },
  cancelled: { bg: '#94A3B8', text: '#fff' },
  no_show: { bg: '#FF6F61', text: '#fff' },
};

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(mon);
    date.setDate(mon.getDate() + i);
    return date;
  });
}

function getMonthDates(baseDate) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const dates = [];
  const startDate = new Date(first);
  startDate.setDate(startDate.getDate() - startDay);
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push(d);
    if (i >= 28 && d.getMonth() !== month && d.getDay() === 1) break;
  }
  return dates;
}

function fmt(d) {
  return d.toISOString().split('T')[0];
}

function isToday(d) {
  return fmt(d) === fmt(new Date());
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 6am to 7pm

export default function ShiftCalendar({ shifts, onReschedule, onShiftClick }) {
  const [viewMode, setViewMode] = useState('week');
  const [baseDate, setBaseDate] = useState(new Date());
  const [dragState, setDragState] = useState(null);
  const calendarRef = useRef(null);

  const navigate = (dir) => {
    const d = new Date(baseDate);
    if (viewMode === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setBaseDate(d);
  };

  const goToday = () => setBaseDate(new Date());

  const weekDates = getWeekDates(baseDate);
  const monthDates = getMonthDates(baseDate);

  const dateRange = viewMode === 'week'
    ? { from: fmt(weekDates[0]), to: fmt(weekDates[6]) }
    : { from: fmt(monthDates[0]), to: fmt(monthDates[monthDates.length - 1]) };

  const shiftsByDate = {};
  shifts.forEach(s => {
    if (!shiftsByDate[s.date]) shiftsByDate[s.date] = [];
    shiftsByDate[s.date].push(s);
  });

  const headerLabel = viewMode === 'week'
    ? `${weekDates[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${weekDates[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : baseDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Drag and drop
  const handleDragStart = useCallback((e, shift) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shift.id);
    setDragState({ shiftId: shift.id, originalDate: shift.date });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetDate) => {
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('text/plain');
    if (!shiftId || !dragState) return;
    const targetDateStr = fmt(targetDate);
    if (dragState.originalDate === targetDateStr) {
      setDragState(null);
      return;
    }
    onReschedule(shiftId, targetDateStr);
    setDragState(null);
  }, [dragState, onReschedule]);

  const ShiftBlock = ({ shift, compact }) => {
    const colors = STATUS_COLORS[shift.status] || STATUS_COLORS.open;
    const isDraggable = ['open', 'assigned'].includes(shift.status);

    return (
      <div
        draggable={isDraggable}
        onDragStart={isDraggable ? (e) => handleDragStart(e, shift) : undefined}
        onClick={() => onShiftClick?.(shift)}
        className={`rounded-md px-2 py-1.5 text-left transition-all duration-150 group ${
          isDraggable ? 'cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-white/20 hover:scale-[1.02]' : 'cursor-pointer'
        } ${compact ? 'mb-0.5' : 'mb-1'}`}
        style={{ backgroundColor: colors.bg, color: colors.text }}
        data-testid={`cal-shift-${shift.id}`}
        title={`${shift.start_time}-${shift.end_time} | ${shift.client_name || 'No client'} | ${shift.caregiver_name || 'Unassigned'} | ${shift.status}`}
      >
        {compact ? (
          <div className="flex items-center gap-1 min-w-0">
            {isDraggable && <GripVertical className="w-2.5 h-2.5 opacity-40 flex-shrink-0" />}
            <span className="text-[10px] font-medium truncate">
              {shift.start_time} {shift.client_name?.split(' ')[0] || '—'}
            </span>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              {isDraggable && <GripVertical className="w-3 h-3 opacity-40 flex-shrink-0 group-hover:opacity-70" />}
              <span className="text-xs font-semibold truncate">{shift.start_time} - {shift.end_time}</span>
            </div>
            <p className="text-[11px] truncate opacity-90 mt-0.5">
              {shift.client_name || 'No client'}
            </p>
            <p className="text-[10px] truncate opacity-70">
              {shift.caregiver_name || 'Unassigned'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Week View
  const WeekView = () => {
    const timeToY = (time) => {
      if (!time) return 0;
      const [h, m] = time.split(':').map(Number);
      return ((h - 6) + m / 60) * 60; // 60px per hour
    };

    return (
      <div className="overflow-auto rounded-xl border border-border bg-card" style={{ maxHeight: 'calc(100vh - 300px)' }} data-testid="week-view">
        <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, 1fr)', minWidth: '800px' }}>
          {/* Header */}
          <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm border-b border-r border-border p-2" />
          {weekDates.map((d, i) => (
            <div key={i} className={`sticky top-0 z-10 backdrop-blur-sm border-b border-border p-2 text-center ${
              isToday(d) ? 'bg-[#00A6A0]/10' : 'bg-muted/80'
            }`}>
              <p className="text-[11px] text-muted-foreground font-medium">{DAYS[i]}</p>
              <p className={`text-lg font-bold ${isToday(d) ? 'text-[#00A6A0]' : 'text-foreground'}`}>
                {d.getDate()}
              </p>
            </div>
          ))}

          {/* Time grid */}
          {HOURS.map(hour => (
            <div key={hour} className="contents">
              <div className="border-r border-b border-border px-1.5 py-0 flex items-start justify-end" style={{ height: '60px' }}>
                <span className="text-[10px] text-muted-foreground -mt-1.5">{hour.toString().padStart(2, '0')}:00</span>
              </div>
              {weekDates.map((d, di) => {
                const dateStr = fmt(d);
                const dayShifts = (shiftsByDate[dateStr] || []).filter(s => {
                  const startH = parseInt(s.start_time?.split(':')[0] || '0');
                  return startH === hour;
                });
                return (
                  <div
                    key={di}
                    className={`border-b border-border relative ${
                      isToday(d) ? 'bg-[#00A6A0]/[0.02]' : ''
                    } ${di < 6 ? 'border-r' : ''}`}
                    style={{ height: '60px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, d)}
                    data-testid={`week-cell-${dateStr}-${hour}`}
                  >
                    <div className="absolute inset-0 p-0.5 overflow-hidden">
                      {dayShifts.map(s => (
                        <ShiftBlock key={s.id} shift={s} compact={dayShifts.length > 1} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Month View
  const MonthView = () => (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="month-view">
      {/* Day headers */}
      <div className="grid grid-cols-7 bg-muted/80">
        {DAYS.map(d => (
          <div key={d} className="px-2 py-2 text-center text-xs font-medium text-muted-foreground border-b border-border">
            {d}
          </div>
        ))}
      </div>
      {/* Date cells */}
      <div className="grid grid-cols-7">
        {monthDates.map((d, i) => {
          const dateStr = fmt(d);
          const dayShifts = shiftsByDate[dateStr] || [];
          const isCurrentMonth = d.getMonth() === baseDate.getMonth();
          return (
            <div
              key={i}
              className={`min-h-[100px] border-b border-r border-border p-1.5 ${
                !isCurrentMonth ? 'opacity-30' : ''
              } ${isToday(d) ? 'bg-[#00A6A0]/5' : ''}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, d)}
              data-testid={`month-cell-${dateStr}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium ${
                  isToday(d) ? 'bg-[#00A6A0] text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-muted-foreground'
                }`}>
                  {d.getDate()}
                </span>
                {dayShifts.length > 0 && (
                  <span className="text-[9px] text-muted-foreground">{dayShifts.length}</span>
                )}
              </div>
              <div className="space-y-0">
                {dayShifts.slice(0, 3).map(s => (
                  <ShiftBlock key={s.id} shift={s} compact />
                ))}
                {dayShifts.length > 3 && (
                  <p className="text-[10px] text-muted-foreground text-center">+{dayShifts.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="shift-calendar">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={() => navigate(-1)} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="border-border text-foreground text-xs h-8" onClick={goToday} data-testid="cal-today">
            Today
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={() => navigate(1)} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-heading font-bold text-foreground ml-2" data-testid="cal-header">
            {headerLabel}
          </h2>
        </div>

        <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
          <Button
            size="sm"
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            className={`text-xs h-7 px-3 ${viewMode === 'week' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}`}
            onClick={() => setViewMode('week')}
            data-testid="view-week"
          >
            Week
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'month' ? 'default' : 'ghost'}
            className={`text-xs h-7 px-3 ${viewMode === 'month' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}`}
            onClick={() => setViewMode('month')}
            data-testid="view-month"
          >
            Month
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px]">
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colors.bg }} />
            <span className="text-muted-foreground capitalize">{status.replace('_', ' ')}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-border">
          <GripVertical className="w-3 h-3 text-muted-foreground" />
          <span className="text-muted-foreground">Drag to reschedule</span>
        </div>
      </div>

      {/* Calendar */}
      {viewMode === 'week' ? <WeekView /> : <MonthView />}

      {/* Range info for parent */}
      <input type="hidden" data-from={dateRange.from} data-to={dateRange.to} data-testid="cal-range" />
    </div>
  );
}
